import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import API from "helpers/api";
import { PRODUCT_UPDATE_NOTIFICATION_REFRESH_EVENT } from "helpers/constants";

// Only the few newest featured updates open the modal; the rest live in the
// Release notes list. Keeps a backlog from feeling like a slog (design §10.4).
const MAX_MODAL_STACK = 3;

function broadcastRefresh() {
	window.dispatchEvent(new CustomEvent(PRODUCT_UPDATE_NOTIFICATION_REFRESH_EVENT));
}

function formatReleased(update) {
	const date = update.publishedAt ? new Date(update.publishedAt) : null;
	const dateText =
		date && !Number.isNaN(date.getTime())
			? new Intl.DateTimeFormat(navigator.language || "en", {
					day: "numeric",
					month: "long",
					year: "numeric",
			  }).format(date)
			: null;

	return ["Released", dateText, update.version]
		.filter(Boolean)
		.join(" · ")
		.replace("Released ·", "Released");
}

function WhatsNewModal() {
	const [updates, setUpdates] = useState([]);
	const [index, setIndex] = useState(0);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		let cancelled = false;

		API.get("/api/product-updates/unread")
			.then((res) => {
				if (cancelled) return;
				const featured = (res?.data || [])
					.filter((u) => u.importance === "featured")
					.slice(0, MAX_MODAL_STACK);

				if (featured.length === 0) return;
				setUpdates(featured);
				setOpen(true);
			})
			.catch(() => {
				// 401 / network - no modal
			});

		return () => {
			cancelled = true;
		};
	}, []);

	const current = updates[index];
	const isLast = index >= updates.length - 1;

	const markCurrentRead = () => {
		if (!current) return Promise.resolve();
		return API.post(`/api/product-updates/${current.id}/mark-read`)
			.then(broadcastRefresh)
			.catch(() => {});
	};

	const advance = () => {
		if (isLast) {
			setOpen(false);
		} else {
			setIndex((i) => i + 1);
		}
	};

	const handleNext = () => {
		markCurrentRead();
		advance();
	};

	// Closing without an explicit choice does NOT mark read - it reappears next login.
	const handleDismiss = () => setOpen(false);

	const handleSkipAll = () => {
		API.post("/api/product-updates/mark-all-read")
			.then(broadcastRefresh)
			.catch(() => {});
		setOpen(false);
	};

	const handleReadArticle = () => {
		markCurrentRead();
		if (current?.externalUrl) {
			window.open(current.externalUrl, "_blank", "noopener,noreferrer");
		}
		advance();
	};

	if (!current) return null;

	const hasImage = Boolean(current.displayImageUrl);

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				// Require an explicit choice; ignore backdrop clicks and Escape so an
				// accidental outside-click can't silently dismiss (and burn) the update.
				if (reason === "backdropClick" || reason === "escapeKeyDown") return;
				handleDismiss();
			}}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { borderRadius: 1, overflow: "hidden" } }}
		>
			<Box
				sx={{
					px: 2,
					py: 1.5,
					borderBottom: "1px solid #C5C5C8",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box
					component="span"
					sx={{
						font: "700 13px 'Roboto Condensed', sans-serif",
						letterSpacing: 0.8,
						textTransform: "uppercase",
						color: "#545353",
					}}
				>
					What's new in EMI3
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					{updates.length > 1 ? (
						<Box component="span" sx={{ fontSize: 13, color: "#545353" }}>
							{index + 1} of {updates.length}
						</Box>
					) : null}
					<IconButton onClick={handleDismiss} size="small" aria-label="close">
						<CloseRoundedIcon fontSize="small" />
					</IconButton>
				</Box>
			</Box>

			{hasImage ? (
				<Box
					component="img"
					src={current.displayImageUrl}
					alt=""
					sx={{
						display: "block",
						width: "100%",
						// Show the whole image (4:3 screenshots, portrait, wide) without
						// cropping; letterbox against the neutral background instead.
						maxHeight: 360,
						objectFit: "contain",
						backgroundColor: "#D2D2D9",
					}}
				/>
			) : null}

			<Box sx={{ px: 2.75, py: 2.5 }}>
				<Box
					component="h2"
					sx={{ font: "700 24px Roboto, sans-serif", color: "#393939", m: 0, mb: 0.75 }}
				>
					{current.title}
				</Box>
				<Box sx={{ fontSize: 14, color: "#545353", mb: 2 }}>
					{formatReleased(current)}
				</Box>
				<Box
					sx={{
						fontSize: 16,
						lineHeight: 1.6,
						color: "#393939",
						mb: current.externalUrl ? 2.25 : 0,
						"& p": { m: 0 },
						"& p + p": { mt: 1.25 },
					}}
					dangerouslySetInnerHTML={{ __html: current.shortDescription }}
				/>
				{current.externalUrl ? (
					<Box
						component="button"
						type="button"
						onClick={handleReadArticle}
						sx={{
							display: "inline-flex",
							alignItems: "center",
							gap: 0.75,
							background: "none",
							border: "none",
							cursor: "pointer",
							p: 0,
							color: "#307AD7",
							font: "700 15px 'Roboto Condensed', sans-serif",
							letterSpacing: 0.3,
							textTransform: "uppercase",
						}}
					>
						Read full article
						<ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
					</Box>
				) : null}
			</Box>

			<Box
				sx={{
					px: 2,
					py: 1.5,
					borderTop: "1px solid #C5C5C8",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box sx={{ display: "inline-flex", gap: 0.75 }}>
					{updates.map((u, i) => (
						<Box
							key={u.id}
							sx={{
								width: 7,
								height: 7,
								borderRadius: "50%",
								backgroundColor: i === index ? "#404149" : "#C5C5C8",
							}}
						/>
					))}
				</Box>
				<Box sx={{ display: "flex", gap: 1 }}>
					{updates.length > 1 ? (
						<Button
							onClick={handleSkipAll}
							variant="outlined"
							sx={{
								color: "#404149",
								borderColor: "#979797",
								font: "700 15px 'Roboto Condensed', sans-serif",
								textTransform: "uppercase",
							}}
						>
							Skip all
						</Button>
					) : null}
					<Button
						onClick={handleNext}
						variant="contained"
						sx={{
							backgroundColor: "#307AD7",
							font: "700 15px 'Roboto Condensed', sans-serif",
							textTransform: "uppercase",
							"&:hover": { backgroundColor: "#2A6CBF" },
						}}
					>
						{isLast ? "Done" : "Next"}
					</Button>
				</Box>
			</Box>
		</Dialog>
	);
}

export default WhatsNewModal;
